import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { HardHat, Trash2, Plus, Sparkles, Layers } from "lucide-react";

const DRIVER_OPTS = [
  { v: "lump", label: "Lump sum" },
  { v: "per_worker", label: "Per worker" },
  { v: "per_mh", label: "Per man-hour" },
  { v: "per_mw", label: "Per MW" },
  { v: "per_trailer", label: "Per trailer" },
  { v: "linear", label: "Per linear ft" },
  { v: "manual", label: "Fixed count" },
];
const BASIS_OPTS = [
  { v: "lump", label: "one-time" },
  { v: "months", label: "/ month" },
  { v: "work_weeks", label: "/ work-week" },
  { v: "cal_weeks", label: "/ cal-week" },
  { v: "work_days", label: "/ work-day" },
  { v: "cal_days", label: "/ cal-day" },
  { v: "man_hours", label: "/ man-hour" },
];
const METRIC_OPTS = [
  { v: "fence_lf", label: "Fence LF" },
  { v: "cable_ft", label: "Cable ft" },
  { v: "acres", label: "Acres" },
];
const gid = () => `gc_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

const Lbl = ({ label, children }) => (
  <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
);

export default function GeneralConditions({ bid, computed, updateBid, readOnly }) {
  const gc = computed?.general_conditions;
  const drivers = computed?.gc_drivers || {};
  const cfg = bid.general_conditions || {};
  const items = cfg.items || [];

  const patch = (next) => updateBid({ general_conditions: { ...cfg, ...next } });
  const enable = () => patch({ enabled: true, include_in_bid: true, items });
  const setItems = (next) => patch({ items: next });
  const upd = (id, changer) => setItems(items.map((it) => (it.id === id ? changer(it) : it)));
  const setField = (id, field, val) => upd(id, (it) => ({ ...it, [field]: val }));
  const setDrv = (id, field, val) => upd(id, (it) => ({ ...it, driver: { ...(it.driver || {}), [field]: val } }));
  const removeItem = (id) => setItems(items.filter((it) => it.id !== id));
  const addCustom = () => setItems([...items, { id: gid(), label: "New line", driver: { type: "lump" }, rate: 0, basis: "lump", enabled: true }]);
  const addSuggestion = (s) => setItems([...items, { id: gid(), rec_key: s.rec_key, label: s.label, note: s.note, group: s.group, driver: { ...s.driver }, rate: s.rate, basis: s.basis, enabled: true }]);

  if (!gc || !gc.enabled) {
    return (
      <div className="p-6 max-w-[1800px] w-full mx-auto">
        <Card className="p-12 text-center" data-testid="gc-disabled">
          <HardHat className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <h2 className="text-sm font-semibold text-slate-700">General Conditions</h2>
          <p className="text-xs text-slate-500 mt-1 mb-4 max-w-md mx-auto">Add-as-needed indirect costs — sanitation, safety, tools, site utilities, cleanup. Fully custom and driver-based. It carries <b>$0</b> until you add lines; nothing is pre-filled. GC Intelligence recommends lines from your man-hours, duration and crew as one-click adds.</p>
          <Button onClick={enable} disabled={readOnly} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="gc-enable">Enable general conditions</Button>
        </Card>
      </div>
    );
  }

  const included = cfg.include_in_bid !== false;
  const gcPct = computed?.totals?.total ? (gc.total / computed.totals.total) * 100 : 0;
  const v = gc.validation || {};

  return (
    <div className="p-5 max-w-[1800px] w-full mx-auto" data-testid="gc-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><HardHat className="w-4 h-4 text-[#003E87]" /> General Conditions</div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5"><span className="text-[10px] uppercase text-slate-400">Duration (mo)</span>
            <Input type="number" step="0.1" value={cfg.duration_months ?? gc.duration_months} onChange={(e) => patch({ duration_months: e.target.value === "" ? null : Number(e.target.value) })} className="h-8 w-20 text-xs font-mono" data-testid="gc-duration" disabled={readOnly} /></div>
          <div className="flex items-center gap-1.5"><span className="text-[10px] uppercase text-slate-400">Include in bid</span>
            <Switch checked={included} onCheckedChange={(val) => patch({ include_in_bid: val })} disabled={readOnly} data-testid="gc-include-toggle" /></div>
          <Button size="sm" variant="outline" disabled={readOnly} className="text-xs" data-testid="gc-reset" title="Back to the company's rates and drivers, with no job figures"
            onClick={async () => {
              if (!window.confirm("Reset general conditions to the company's rates and drivers? This bid's counts, overrides and lump amounts are cleared.")) return;
              try { const { data } = await api.post(`/bids/${bid.bid_id}/general-conditions/reset`); await updateBid({ general_conditions: data.general_conditions }); toast.success("General conditions reset to company rates and drivers"); }
              catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
            }}>Reset GC</Button>
          <Button size="sm" variant="ghost" onClick={() => patch({ enabled: false })} disabled={readOnly} className="text-slate-400 hover:text-rose-600 text-xs" data-testid="gc-disable">Turn off</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Card className="p-3" data-testid="gc-total-card"><div className="text-[10px] uppercase text-slate-400">GC total</div><div className="text-xl font-bold font-mono text-black" data-testid="gc-total">{money(gc.total)}</div><div className="text-[10px] text-slate-400">{gcPct ? `${gcPct.toFixed(1)}% of estimate` : (included ? "included in the bid" : "excluded")}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase text-slate-400">Drivers</div><div className="text-sm font-mono text-slate-800">{v.peak_hc || 0} peak · {v.months || 0} mo</div><div className="text-[10px] text-slate-400">{num(drivers.total_mh || 0)} MH · {v.work_days || 0} WD</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase text-slate-400">GC / MW · / MH</div><div className="text-sm font-mono text-slate-800">{gc.metrics?.per_mw != null ? money(gc.metrics.per_mw) : "—"}</div><div className="text-[10px] text-slate-400">{gc.metrics?.per_mh != null ? `$${gc.metrics.per_mh}/MH` : ""}</div></Card>
      </div>

      {!included && <div className="mb-4 rounded-md border border-slate-300 bg-slate-50 px-4 py-2 text-xs text-slate-600" data-testid="gc-excluded-note">General conditions are <b>configured but excluded</b> from this bid's total. Flip “Include in bid” to roll them in.</div>}

      {(gc.suggestions || []).length > 0 && (
        <Card className="p-4 mb-4 border-[#F4C400]/40 bg-[#F4C400]/[0.05]" data-testid="gc-intelligence">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2"><Sparkles className="w-3.5 h-3.5 text-[#B8860B]" /> GC Intelligence — recommended lines <span className="text-[10px] font-normal text-slate-400">tuned to {v.peak_hc || 0} peak workers · {num(drivers.total_mh || 0)} MH · {v.months || 0} mo</span></div>
          <div className="flex flex-wrap gap-2">
            {gc.suggestions.map((s) => (
              <button key={s.rec_key} disabled={readOnly} onClick={() => addSuggestion(s)} data-testid={`gc-suggestion-add-${s.rec_key}`}
                className="group flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-3 pr-2 py-1 text-[11px] hover:border-[#003E87] transition-colors disabled:opacity-50">
                <span className="text-slate-700">{s.label}</span>
                <span className="font-mono text-slate-400 group-hover:text-slate-600">{s.projected_cost ? money(s.projected_cost) : "$0"}</span>
                <Plus className="w-3 h-3 text-[#003E87]" />
              </button>
            ))}
          </div>
        </Card>
      )}

      {!readOnly && (
        <div className="mb-3">
          <Button size="sm" variant="outline" onClick={addCustom} data-testid="gc-add-custom" className="text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add custom line</Button>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-8 text-center" data-testid="gc-empty"><div className="text-xs text-slate-500">No general-conditions lines yet.</div><div className="text-[11px] text-slate-400 mt-0.5">Add from GC Intelligence above, or build a custom driver-based line — each starts at $0.</div></Card>
      ) : (
        <div className="space-y-3">
          {gc.groups.map((grp) => (
            <Card key={grp.group} className="overflow-hidden" data-testid={`gc-group-${grp.group}`}>
              <div className="flex items-center justify-between px-4 py-2 bg-[#003E87]/[0.04]">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#003E87]"><Layers className="w-3.5 h-3.5" /> {grp.group}</div>
                <div className="text-sm font-mono font-semibold text-slate-700" data-testid={`gc-subtotal-${grp.group}`}>{money(grp.subtotal)}</div>
              </div>
              <div className="divide-y divide-slate-50">
                {grp.lines.map((l) => {
                  const it = items.find((x) => x.id === l.id) || {};
                  const drv = it.driver || {};
                  return (
                    <div key={l.id} className={`px-4 py-2.5 ${!l.enabled ? "opacity-40" : ""}`} data-testid={`gc-item-${l.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Switch checked={l.enabled} onCheckedChange={(val) => setField(l.id, "enabled", val)} disabled={readOnly} data-testid={`gc-item-enable-${l.id}`} />
                          <input disabled={readOnly} value={it.label || ""} onChange={(e) => setField(l.id, "label", e.target.value)} data-testid={`gc-item-label-${l.id}`}
                            className="flex-1 min-w-0 text-xs font-medium text-slate-800 bg-transparent border border-transparent hover:border-slate-200 focus:border-[#003E87] focus:bg-white rounded px-1.5 py-1 outline-none" />
                        </div>
                        <div className="text-sm font-mono font-semibold text-slate-800 shrink-0 w-24 text-right" data-testid={`gc-item-cost-${l.id}`}>{money(l.cost)}</div>
                        {!readOnly && <button onClick={() => removeItem(l.id)} className="text-slate-300 hover:text-rose-600" data-testid={`gc-item-remove-${l.id}`}><Trash2 className="w-4 h-4" /></button>}
                      </div>
                      {!readOnly && (
                        <div className="flex items-end gap-2 mt-2 flex-wrap pl-9">
                          <Lbl label="Driver">
                            <select value={drv.type || "lump"} onChange={(e) => setDrv(l.id, "type", e.target.value)} data-testid={`gc-item-driver-${l.id}`} className="h-7 text-[11px] border border-slate-200 rounded px-1.5 bg-white">
                              {DRIVER_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                            </select>
                          </Lbl>
                          {["per_worker", "per_mw", "per_trailer", "manual"].includes(drv.type) && (
                            <Lbl label={drv.type === "manual" ? "count" : "1 per"}><Input type="number" step="any" value={drv.ratio ?? ""} onChange={(e) => setDrv(l.id, "ratio", e.target.value === "" ? "" : Number(e.target.value))} className="h-7 w-16 text-[11px] font-mono" data-testid={`gc-item-ratio-${l.id}`} /></Lbl>
                          )}
                          {drv.type === "linear" && (
                            <Lbl label="metric"><select value={drv.metric || "fence_lf"} onChange={(e) => setDrv(l.id, "metric", e.target.value)} data-testid={`gc-item-metric-${l.id}`} className="h-7 text-[11px] border border-slate-200 rounded px-1.5 bg-white">{METRIC_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}</select></Lbl>
                          )}
                          {drv.type === "per_worker" && (
                            <Lbl label="counts as trailer?"><div className="h-7 flex items-center"><Switch checked={!!drv.is_trailer} onCheckedChange={(val) => setDrv(l.id, "is_trailer", val)} data-testid={`gc-item-trailer-${l.id}`} /></div></Lbl>
                          )}
                          <Lbl label={drv.type === "per_mh" ? "$/MH" : "rate $"}><Input type="number" step="any" value={it.rate ?? ""} onChange={(e) => setField(l.id, "rate", e.target.value === "" ? "" : Number(e.target.value))} className="h-7 w-20 text-[11px] font-mono" data-testid={`gc-item-rate-${l.id}`} /></Lbl>
                          <Lbl label="basis"><select value={it.basis || "months"} onChange={(e) => setField(l.id, "basis", e.target.value)} data-testid={`gc-item-basis-${l.id}`} className="h-7 text-[11px] border border-slate-200 rounded px-1.5 bg-white">{BASIS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}</select></Lbl>
                          <Lbl label="count (override)"><Input type="number" step="any" value={it.count_override ?? ""} placeholder={String(l.count)} onChange={(e) => setField(l.id, "count_override", e.target.value === "" ? null : Number(e.target.value))} className="h-7 w-20 text-[11px] font-mono" data-testid={`gc-item-count-${l.id}`} /></Lbl>
                          <span className="text-[10px] text-slate-400 font-mono self-center" data-testid={`gc-item-detail-${l.id}`}>= {l.detail}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[10px] text-slate-400 mt-3">Every line is a driver × rate × basis, computed live off your priced scopes ({v.peak_hc || 0} peak workers · {num(drivers.total_mh || 0)} MH · {v.months || 0} mo). Nothing is pre-filled — add from GC Intelligence or build custom lines. GC rolls into the bid's direct cost only while “Include in bid” is on.</p>
    </div>
  );
}
